// @workflow tasks.show-in-3d
/**
 * The task UI tool: `ui.tasks.show_in_3d { taskIds (1–500), facilityId?, mode? }`.
 *
 * - **On the facility's task board** it runs what the board's own 3D buttons run (src/actions/tasks.js
 *   `showTasksInViewer`): the tasks' effective tags open in the board's viewer panel, task-coloured,
 *   on the tab the task resolver picks (3D, or 360). `mode: 'status'` colours them by status.
 * - **Anywhere else** the facility's 3D model is prepared like focus_tags (viewerPrepare.js:
 *   open the facility, a 3D pane, wait) and the tasks' tags are isolated there (viewerTools.js
 *   `isolateTags`). Colouring by status needs the board's colouring panel, so it is not available there.
 *
 * Success: `{ shown: [taskIds], skipped: [{ id, reason }] }`, ids as asked, with reason `no_tags`
 * (no linked tag, or not a task here), or off the board `not_found` / `no_place` for its tags.
 * Off the board, `unlinkedFrom360: true` when the 3D view was linked to a 360 and AVA unlinked it first.
 * Pure decisions only; `services/taskHost.js` wires the host to the action.
 *
 * Host contract (`host.tasks`):
 *   boardFacilityId() → string|null              the facility of the task board on screen
 *   showOnBoard({ taskIds, mode }) → Promise<{ status, shown, skipped }>   actions/tasks.js
 *   tagsOf(taskIds) → Promise<Array<{ id, tagIds }>>   each task's effective tag ids
 * and `host.viewer` (viewerTools.js, viewerPrepare.js) off the board.
 * No Vue or path-alias imports, so `npm run test:unit` (node --test) can load it directly.
 */
import { UI_TOOL, ok, notAvailable, failed } from './uiToolContract.js';
import { VIEWER_DETAIL, SKIP_REASON, sameId } from './viewerContract.js';
import { cleanFacilityId, cleanIdList } from './viewerArgs.js';
import { prepareViewer } from './viewerPrepare.js';
import { isolateTags } from './viewerTools.js';
import { SHOW_TASKS_MODE, SHOW_TASKS_STATUS, TASK_SKIP_REASON } from '../../../actions/taskContract.js';
import { CAPABILITY } from '../../../actions/permissions.js';

/** The task list page's biggest page (List.vue itemsPerPageOptions) and the tag tools' id length. */
export const TASK_LIMITS = Object.freeze({ MAX_TASK_IDS: 500, MAX_TASK_ID_LENGTH: 64 });

/** Plain words for the model. */
export const TASK_DETAIL = Object.freeze({
  NONE_SHOWN: 'None of these tasks have tags to show in 3D.',
  BOARD_CLOSED: 'The task board closed before AVA could show the tasks.',
  STATUS_NEEDS_BOARD: 'Colouring tasks by status works on the task board only. Open the task board, then try again.',
});

const isPlainObject = (value) => value !== null && typeof value === 'object' && !Array.isArray(value);
const isAbsent = (value) => value === undefined || value === null;
const tasksOf = (host) => (host && host.tasks) || null;

/** `{ taskIds, facilityId?, mode? }` → `{ taskIds, facilityId, mode }` or null. */
export function checkTaskArgs(args) {
  if (!isPlainObject(args)) return null;
  const taskIds = cleanIdList(args.taskIds, { maxCount: TASK_LIMITS.MAX_TASK_IDS, maxLength: TASK_LIMITS.MAX_TASK_ID_LENGTH });
  const facilityId = cleanFacilityId(args.facilityId);
  const mode = isAbsent(args.mode) ? SHOW_TASKS_MODE.ISOLATE : args.mode;
  if (!taskIds || facilityId === undefined || !Object.values(SHOW_TASKS_MODE).includes(mode)) return null;
  return { taskIds, facilityId, mode };
}

/** The task board on screen shows the facility asked for (or no facility was named). */
function onTheBoard(host, { facilityId }) {
  const boardFacility = tasksOf(host).boardFacilityId();
  return !!boardFacility && (!facilityId || sameId(boardFacility, facilityId));
}

/** On the board nothing needs preparing; elsewhere the 3D model is, like focus_tags. */
function prepareTasks(host, input) {
  if (!tasksOf(host)) return notAvailable(VIEWER_DETAIL.NO_VIEWER);
  if (onTheBoard(host, input)) return null;
  if (input.mode === SHOW_TASKS_MODE.STATUS) return notAvailable(TASK_DETAIL.STATUS_NEEDS_BOARD);
  return prepareViewer(host, input.facilityId);
}

function boardResult(outcome) {
  const status = outcome && outcome.status;
  if (status === SHOW_TASKS_STATUS.SHOWN) return ok({ shown: outcome.shown, skipped: outcome.skipped });
  if (status === SHOW_TASKS_STATUS.NOTHING) return failed(TASK_DETAIL.NONE_SHOWN);
  return notAvailable(TASK_DETAIL.BOARD_CLOSED);
}

/** A task whose tags were all skipped: `no_place` if any is in the facility without a place, else `not_found`. */
function taskSkipReason(tagIds, skippedReasons) {
  return tagIds.some((id) => skippedReasons.get(id.toLowerCase()) === SKIP_REASON.NO_PLACE) ? SKIP_REASON.NO_PLACE : SKIP_REASON.NOT_FOUND;
}

/** Per-tag `{ shown, skipped }` → per task: shown when any of its tags is on screen. */
function perTask(tasks, { shown, skipped }) {
  const onScreen = new Set(shown.map((id) => id.toLowerCase()));
  const skippedReasons = new Map(skipped.map((s) => [s.id.toLowerCase(), s.reason]));
  const result = { shown: [], skipped: [] };
  tasks.forEach(({ id, tagIds }) => {
    if (!tagIds.length) result.skipped.push({ id, reason: TASK_SKIP_REASON.NO_TAGS });
    else if (tagIds.some((tagId) => onScreen.has(tagId.toLowerCase()))) result.shown.push(id);
    else result.skipped.push({ id, reason: taskSkipReason(tagIds, skippedReasons) });
  });
  return result;
}

async function showInModel({ taskIds }, host) {
  const tasks = await tasksOf(host).tagsOf(taskIds);
  const tagIds = [...new Set(tasks.flatMap((task) => task.tagIds))];
  if (!tagIds.length) return failed(TASK_DETAIL.NONE_SHOWN);
  const result = await isolateTags(host, tagIds);
  if (!result.ok) return result;
  // Per task instead of per tag; any other fact of the answer (an unlinked 360, AVA-744) is kept.
  const { shown, skipped, ...facts } = result.data;
  return ok({ ...perTask(tasks, { shown, skipped }), ...facts });
}

async function runShow(input, host) {
  if (onTheBoard(host, input)) return boardResult(await tasksOf(host).showOnBoard({ taskIds: input.taskIds, mode: input.mode }));
  return showInModel(input, host);
}

/** Registry entries. `capability`: the runner checks `host.can` after `check`, before `ready`. */
export const TASK_ACTIONS = Object.freeze({
  [UI_TOOL.TASKS_SHOW_IN_3D]: {
    capability: CAPABILITY.TASKS_SHOW_IN_3D,
    check: checkTaskArgs,
    ready: prepareTasks,
    run: runShow,
  },
});
